import React from "react";
import { useDisclosure, useToast, Box, Text } from "@chakra-ui/react";
import DeliveryOptionsModal from "./DeliveryOptionsModal";
import {
  DeliveryOptionsLaunchConfig,
  DeliveryOptionsSupplierInput,
  EnrichedSupplierForDelivery,
  NormalizedDeliverySelection,
} from "./types";
import services from "../../views/Orders/services";
import { META_INFO_KEYS } from "../../utils/constants";
import { getCurrentUTCTimestamp } from "../../utils/dateResolvers";
import { useOrdersStore } from "../../views/Orders/store/useOrdersStore";
import { extractApiErrorMessage, logApiError } from "./utils/apiErrorUtils";
import { useGetOrderMediaConfig } from "../../views/PlaceOrderTest/queryHooks";

export const useDeliveryOptionsLauncher = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const { data: configData } = useGetOrderMediaConfig();
  const setOrderMetaInfoWithOrderId = useOrdersStore(
    (state) => state.setOrderMetaInfoWithOrderId
  );
  
  // Parse feature flag - handle both JSON string and boolean/string values
  const ff_share_enquiry_estimate_to_customer: boolean = React.useMemo(() => {
    const flagValue = configData?.ff_share_enquiry_estimate_to_customer;
    return flagValue === "true";
    
  }, [configData?.ff_share_enquiry_estimate_to_customer]);

  // Get maximum split count allowed for showing share estimate button
  const maxSplitAllowedShareToCustomer: number = React.useMemo(() => {
    return configData?.max_split_allowed_share_to_customer ?? 1;
  }, [configData?.max_split_allowed_share_to_customer]);
  const [isFetchingDeliveryOptions, setIsFetchingDeliveryOptions] =
    React.useState(false);
  const [orderNo, setOrderNo] = React.useState<string>("");
  const [expiresAt, setExpiresAt] = React.useState<string>("");
  const [estimateNo, setEstimateNo] = React.useState<string>("");
  const [isEstimateShared, setIsEstimateShared] = React.useState<boolean>(false);
  const [modalOpenedAt, setModalOpenedAt] = React.useState<string>("");
  const [quickEtasMins, setQuickEtasMins] = React.useState<
    string | undefined
  >();
  const [suppliers, setSuppliers] = React.useState<
    EnrichedSupplierForDelivery[]
  >([]);
  const [onCancelOrder, setOnCancelOrder] = React.useState<
    ((reason: string) => Promise<void> | void) | undefined
  >();
  const [onHoldOrder, setOnHoldOrder] = React.useState<
    (() => Promise<void> | void) | undefined
  >();
  const [onClearBucket, setOnClearBucket] = React.useState<
    (() => void) | undefined
  >();
  const [onSelectionCloseCallback, setOnSelectionCloseCallback] =
    React.useState<(() => void) | undefined>();
  const [onOuterCloseCallback, setOnOuterCloseCallback] = React.useState<
    (() => void) | undefined
  >();
  const [refetchOrders, setRefetchOrders] = React.useState<
    (() => void) | undefined
  >();
  const [orderCancellationReasons, setOrderCancellationReasons] =
    React.useState<string[]>();

  const onConfirmRef = React.useRef<
    | ((
        selections: NormalizedDeliverySelection[],
        splitData: EnrichedSupplierForDelivery[],
        estimateNo: string,
      ) => void)
    | null
  >(null);
  const onShareEstimateRequestedRef = React.useRef<(() => void) | null>(null);
  const supplierInputsRef = React.useRef<DeliveryOptionsSupplierInput[]>([]);

  const fetchAndEnrichSuppliers = React.useCallback(
    async (
      orderNo: string,
      suppliers: DeliveryOptionsSupplierInput[]
    ): Promise<{
      enrichedSuppliers: EnrichedSupplierForDelivery[];
      expiresAt: string;
      estimateNo: string;
    }> => {
      const payload = {
        splits: suppliers.map((supplier) => ({
          supplierId: supplier.supplierId,
          items: supplier.items.map((item) => ({
            partId: item.partId,
            qty: item.quantity,
          })),
        })),
      };

      const response = await services.fetchDeliveryOptions(orderNo, payload);
      const splitDataMap = new Map<string | null, any>(
        response.splits.map((split: any) => [split.supplierId ?? null, split])
      );

      const enrichedSuppliers: EnrichedSupplierForDelivery[] = suppliers.map(
        (s) => {
          const key = s.supplierId ?? null;
          const splitData = splitDataMap.get(key);
          const apiOptions = (splitData?.deliveryOptions || []) as any[];

          return {
            splitId: splitData?._id || "",
            supplierId: s.supplierId,
            supplierName: s.supplierName,
            distanceKm: s.distanceKm,
            supplierCity: s.supplierCity,
            parts: s.items,
            deliveryOptions: apiOptions,
            buyTime: splitData?.buyTime,
            vehicleType: splitData?.vehicleType,
          };
        }
      );

      return {
        enrichedSuppliers,
        expiresAt: response.expiresAt,
        estimateNo: response.estimateNo,
      };
    },
    []
  );

  const launchDeliveryOptionsModal = React.useCallback(
    async (config: DeliveryOptionsLaunchConfig) => {
      setIsFetchingDeliveryOptions(true);
      try {
        setOrderNo(config.orderNo);
        setQuickEtasMins(config.quickEtasMins);
        setOnCancelOrder(() => config.onCancelOrder);
        setOnHoldOrder(() => config.onHoldOrder);
        setOnClearBucket(() => config.onClearBucket);
        setRefetchOrders(() => config.refetchOrders);
        setOnSelectionCloseCallback(() => config.onSelectionClose);
        setOnOuterCloseCallback(() => config.onClose);
        setOrderCancellationReasons(config.orderCancellationReasons);
        onConfirmRef.current = config.onConfirm;
        onShareEstimateRequestedRef.current = config.onShareEstimateRequested || null;
        supplierInputsRef.current = config.suppliers;
        setModalOpenedAt(new Date().toISOString());
        const currentUTCTimestamp = getCurrentUTCTimestamp();
        const { enrichedSuppliers, expiresAt, estimateNo } = await fetchAndEnrichSuppliers(
          config.orderNo,
          config.suppliers
        );

        setSuppliers(enrichedSuppliers);
        setExpiresAt(expiresAt);
        setEstimateNo(estimateNo);
        setIsEstimateShared(false); // Reset shared state when opening modal with new estimate
        onOpen();

        // Update meta info with estimateSharedAt timestamp in the background (non-blocking)
        if (config.orderId) {
          services
            .updateOrderMetaInfo(config.orderId, {
              [META_INFO_KEYS.ESTIMATE_SHARED_AT]: currentUTCTimestamp,
            })
            .then(() => {
              setOrderMetaInfoWithOrderId(config.orderId, {
                [META_INFO_KEYS.ESTIMATE_SHARED_AT]: currentUTCTimestamp,
              });
            })
            .catch((error) => {
              // Non-blocking: Log error but don't prevent modal from opening
              console.error("Failed to update estimateSharedAt:", error);
            });
        }
      } catch (error: any) {
        // Log full error details for debugging
        logApiError(error, "fetchDeliveryOptions");

        // Extract and display user-friendly error message
        const errorMessage = extractApiErrorMessage(
          error,
          "Failed to fetch delivery options. Please try again."
        );

        // Split message by newlines and create a formatted component for multi-line display
        const messageLines = errorMessage.split("\n");
        const isMultiLine = messageLines.length > 1;

        toast({
          title: "Failed to fetch delivery options",
          description: isMultiLine ? undefined : errorMessage,
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top",
          render: isMultiLine
            ? () => (
                <Box
                  p="4"
                  borderRadius="md"
                  bg="red.500"
                  color="white"
                  boxShadow="lg"
                >
                  <Text fontWeight="bold" fontSize="md" mb="2">
                    Failed to fetch delivery options
                  </Text>
                  <Box>
                    {messageLines.map((line, index) => (
                      <Text key={index} fontSize="sm" mb="1">
                        {index + 1}. {line}
                      </Text>
                    ))}
                  </Box>
                </Box>
              )
            : undefined,
        });
      } finally {
        setIsFetchingDeliveryOptions(false);
      }
    },
    [onOpen, fetchAndEnrichSuppliers, toast, setOrderMetaInfoWithOrderId]
  );

  const executeShareEstimate = React.useCallback(async () => {
    if (!estimateNo) {
      toast({
        title: "Share Estimate",
        description: "Estimate number is missing.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
      return;
    }

    try {
      await services.shareEstimate(estimateNo);
      setIsEstimateShared(true);
      toast({
        title: "Share Estimate",
        description: "Estimate shared successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top",
      });
    } catch (error: any) {
      logApiError(error, "shareEstimate");
      const errorMessage = extractApiErrorMessage(
        error,
        "Failed to share estimate. Please try again."
      );
      toast({
        title: "Share Estimate",
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top",
      });
    }
  }, [estimateNo, toast]);

  const handleShareEstimate = React.useCallback(async () => {
    if (onShareEstimateRequestedRef.current) {
      onShareEstimateRequestedRef.current();
      return;
    }
    await executeShareEstimate();
  }, [executeShareEstimate]);

  const handleConfirm = React.useCallback(
    (
      selections: NormalizedDeliverySelection[],
      splitData: EnrichedSupplierForDelivery[]
    ) => {
      onClose();
      onConfirmRef.current?.(selections, splitData, estimateNo);
    },
    [onClose, estimateNo],
  );

  const handleHoldOrder = React.useCallback(async () => {
    if (onHoldOrder) {
      await onHoldOrder();
    }

    if (estimateNo && suppliers.length > 0) {
      const splitsToHold = suppliers.filter((s) => s.splitId);
      const results = await Promise.allSettled(
        splitsToHold.map((s) =>
          services.holdEstimateSplit(estimateNo, s.splitId),
        ),
      );

      const failures = results
        .map((r, i) => (r.status === "rejected" ? splitsToHold[i] : null))
        .filter(Boolean);

      if (failures.length > 0) {
        toast({
          title: "Hold Estimate",
          description: `Failed to hold ${failures.length}/${splitsToHold.length} splits.`,
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top",
        });
      } else {
        toast({
          title: "Hold Estimate",
          description: "All splits held successfully.",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
      }
    }

    refetchOrders?.();
    onClose();
  }, [onHoldOrder, onClose, estimateNo, suppliers, toast, refetchOrders]);

  const handleClearBucketAndClose = React.useCallback(() => {
    onClearBucket?.();
    onClose();
  }, [onClearBucket, onClose]);


  const handleCancelEstimate = React.useCallback(
    async (reason: string) => {
      if (onCancelOrder) {
        try {
          await onCancelOrder(reason);
          toast({
            title: "Cancel Enquiry",
            description: "Enquiry cancelled successfully.",
            status: "success",
            duration: 3000,
            isClosable: true,
            position: "top",
          });
        } catch (error: any) {
          const errorMessage = extractApiErrorMessage(
            error,
            "Failed to cancel enquiry.",
          );
          toast({
            title: "Cancel Enquiry",
            description: errorMessage,
            status: "error",
            duration: 5000,
            isClosable: true,
            position: "top",
          });
        }
      }

      if (estimateNo && suppliers.length > 0) {
        const splitsToCancel = suppliers.filter((s) => s.splitId);
        const results = await Promise.allSettled(
          splitsToCancel.map((s) =>
            services.cancelEstimateSplit(estimateNo, s.splitId),
          ),
        );

        const failures = results
          .map((r, i) => (r.status === "rejected" ? splitsToCancel[i] : null))
          .filter(Boolean);

        if (failures.length > 0) {
          toast({
            title: "Cancel Enquiry",
            description: `Failed to cancel ${failures.length}/${splitsToCancel.length} splits.`,
            status: "error",
            duration: 5000,
            isClosable: true,
            position: "top",
          });
        } else {
          toast({
            title: "Cancel Estimate",
            description: "All splits cancelled successfully.",
            status: "success",
            duration: 3000,
            isClosable: true,
            position: "top",
          });
        }
      }

      refetchOrders?.();
      onSelectionCloseCallback?.();
      onOuterCloseCallback?.();
    },
    [
      onCancelOrder,
      estimateNo,
      suppliers,
      toast,
      refetchOrders,
      onSelectionCloseCallback,
      onOuterCloseCallback,
    ],
  );

  const deliveryOptionsModal = (
    <DeliveryOptionsModal
      isOpen={isOpen}
      onClose={onClose}
      orderNo={orderNo}
      suppliers={suppliers}
      expiresAt={expiresAt}
      onShareEstimate={ff_share_enquiry_estimate_to_customer ? handleShareEstimate : undefined}
      isEstimateShared={isEstimateShared}
      maxSplitAllowedShareToCustomer={maxSplitAllowedShareToCustomer}
      onConfirm={handleConfirm}
      onHoldOrder={handleHoldOrder}
      onClearBucket={handleClearBucketAndClose}
      quickEtasMins={quickEtasMins}
      directDeliveryMinimumDeliveryCost={
        configData?.direct_delivery_min_delivery_cost_rs
      }
      onCancelOrder={handleCancelEstimate}
      orderCancellationReasons={orderCancellationReasons}
      modalOpenedAt={modalOpenedAt}
      estimateNo={estimateNo}
    />
  );

  return {
    isDeliveryOptionsOpen: isOpen,
    isFetchingDeliveryOptions,
    launchDeliveryOptionsModal,
    deliveryOptionsModal,
    estimateNo,
    executeShareEstimate,
  };
};

export default useDeliveryOptionsLauncher;
